import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import App from "./App";
import { HelmetProvider } from "react-helmet-async";
import { queryClient } from "./config/queryClient";
import { initializeAnalytics } from "./utils/analytics";
import { initWebVitals } from "./utils/webVitals";
import { suppressBenignResizeObserverErrors } from "./utils/suppressResizeObserverError";
import { prefetchSiteContact } from "./services/siteContactApi";
import { initFrontendSentry } from "./config/sentry";

initFrontendSentry();

// Overlay do CRA trata ResizeObserver loop como erro fatal (falso positivo).
suppressBenignResizeObserverErrors();
prefetchSiteContact();

const DevQueryTools =
  process.env.NODE_ENV === "development"
    ? lazy(() =>
        import("@tanstack/react-query-devtools")
          .then((mod) => {
            const Devtools =
              mod.ReactQueryDevtools || mod.default?.ReactQueryDevtools;
            function DevtoolsGate() {
              if (!Devtools) return null;
              return (
                <Devtools
                  initialIsOpen={false}
                  buttonPosition="bottom-left"
                />
              );
            }
            return { default: DevtoolsGate };
          })
          .catch(() => ({ default: function DevtoolsUnavailable() { return null; } }))
      )
    : function DevQueryToolsOff() {
        return null;
      };

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <App />
      <Suspense fallback={null}>
        <DevQueryTools />
      </Suspense>
    </QueryClientProvider>
  </HelmetProvider>
);

// Registrar service worker somente para Push Notifications
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Log apenas em desenvolvimento (service worker é crítico)
        if (process.env.NODE_ENV === "development") {
          console.log("✅ Service Worker registrado com sucesso", registration);
        }
      })
      .catch((error) => {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === "development") {
          console.error("❌ Falha ao registrar Service Worker", error);
        }
      });
  });
}

// Inicializar analytics de forma não bloqueante
if (typeof window !== "undefined") {
  // Usar requestIdleCallback se disponível, senão setTimeout
  if (window.requestIdleCallback) {
    window.requestIdleCallback(
      () => {
        initializeAnalytics();
      },
      { timeout: 2000 }
    );
  } else {
    setTimeout(() => {
      initializeAnalytics();
    }, 100);
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
if (typeof window !== "undefined") {
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => initWebVitals(), { timeout: 4000 });
  } else {
    window.setTimeout(() => initWebVitals(), 2000);
  }
}
